// Pure read-back helpers for My Schedule (kept out of sessions.get.js for testability).
export function deriveUserAdded(sessions, scheduleIds) {
    const set = scheduleIds instanceof Set ? scheduleIds : new Set(scheduleIds || [])
    return (sessions || []).map((s) => ({ ...s, user_added: set.has(s.unique_id) }))
}

export function filterToSchedule(sessions, scheduleIds) {
    const set = scheduleIds instanceof Set ? scheduleIds : new Set(scheduleIds || [])
    return (sessions || []).filter((s) => set.has(s.unique_id))
}

/**
 * Per-user SEAT state, derived the same way and for the same reason as
 * user_added: it cannot be baked into a cached feed.
 *
 * The feed never sets user_has_seat on the live path — only the dev `randomize`
 * branch does — so every card read it as false. The visible effect was that
 * saving a seat left the card unchanged: `action-required` is defined partly by
 * `!user_has_seat`, so it stayed true (and the card stayed orange) however many
 * seats you saved, and the saved-seat treatment was unreachable entirely.
 *
 * Seat and waitlist are kept SEPARATE. A waitlist place is not a saved seat
 * (rule 5) — it must not show the saved-seat treatment — but it does mean the
 * attendee has already acted, so it should not read as action-required either.
 *
 * The lists hold BIZZABO ids, so they are compared against bizzabo_session_id —
 * never unique_id, which appears in neither. Compared as strings because the
 * two sources disagree on numeric vs string.
 *
 * A registration only counts as a SEAT on a session that actually holds seats.
 * `sessionRegistration` is every Bizzabo session registration the attendee has,
 * and since the schedule sync began mirroring unlimited sessions up to Bizzabo
 * that list covers most of their schedule. Reading it as "saved a seat" made
 * every ordinary session show the saved-seat treatment, and — because the remove
 * confirmation only appears when a seat is held — told attendees that a session
 * with no capacity limit "requires a saved seat in order to attend" (UNB-733).
 *
 * The registration is real; it just is not a seat. Only a limited-capacity
 * session has one of those.
 */
export function deriveSeatState(sessions, user) {
    const seats = new Set((user?.sessionRegistration || []).map(String))
    const waits = new Set((user?.sessionWaitlist || []).map(String))
    return (sessions || []).map((s) => {
        const bz = s?.bizzabo_session_id
        if (!bz || s?.session_reservations !== true) {
            return { ...s, user_has_seat: false, user_waitlisted: false }
        }
        const key = String(bz)
        return { ...s, user_has_seat: seats.has(key), user_waitlisted: waits.has(key) }
    })
}
